import { useContext, useState } from 'react'
import { GridItemChangeEvent } from '@progress/kendo-react-grid'
import { ExchangeRate } from 'api/Clients/Types/ExchangeRate'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useApiClient } from 'utils/api/ApiClient'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { NumbersAndDotOnlyPattern } from 'utils/formValidation'

export interface RateFormData {
  baseCurrency: string
  quoteCurrency: string
  rate: number
  originalRate: number
  withdrawalRate: number
  newDepositRate: number
  newWithdrawalRate: number
  inEdit?: string | boolean
  errors?: {
    newDepositRate?: string
    newWithdrawalRate?: string
  }
}

export const useCurrencyMarkups = (
  quoteCurrency: string,
  baseCurrencies: string[],
  withdrawalPercentage: number
) => {
  const [editableRates, setEditableRates] = useState<RateFormData[]>([])
  const exchangeRatesApiClient = useApiClient(ExchangeRatesApiClient)
  const [currencyMarkupRates, setCurrencyMarkupRates] = useState<ExchangeRate[]>([])
  const setToast = useContext(ToastContext)

  const [invalidRateMessage, setInvalidRateMessage] = useState<string>('')

  const validateRates = (rates: RateFormData[]) => {
    const updated = rates.map((rate) => {
      const errors: RateFormData['errors'] = {}

      if (!(rate.newDepositRate > 0)) {
        errors.newDepositRate = 'Deposit rate must be a positive number.'
      }

      if (!(rate.newWithdrawalRate > 0)) {
        errors.newWithdrawalRate = 'Withdrawal rate must be a positive number.'
      }

      return { ...rate, errors }
    })

    setEditableRates(updated)

    const hasErrors = updated.some((r) => r.errors && Object.keys(r.errors).length > 0)
    setInvalidRateMessage(hasErrors ? 'Deposit and/or widthdrawal rate is invalid' : '')
  }

  const onClickFetch = async (currencyId: string) => {
    if (!currencyId || !baseCurrencies.length) return

    const queryParams = baseCurrencies
      .map((id) => `ToBaseCurrencyIds=${encodeURIComponent(id)}`)
      .join('&')

    try {
      const data = await exchangeRatesApiClient.fetchRateMarkupsAllForCurrency(
        currencyId,
        queryParams
      )

      setCurrencyMarkupRates(data)

      const formattedRates = data.map((rate) => ({
        baseCurrency: rate.baseCurrency,
        quoteCurrency: rate.quoteCurrency,
        rate: rate.rate,
        originalRate: rate.originalRate,
        withdrawalRate: rate.withdrawalRate,
        newDepositRate: rate.rate,
        newWithdrawalRate: rate.withdrawalRate,
        inEdit: undefined,
      }))

      setEditableRates(formattedRates)
      validateRates(formattedRates)
    } catch (e) {
      setToast(errorToast('Failed to fetch currencies.' + e))
    }
  }

  const handleSubmit = async () => {
    const changedRates = editableRates.filter((rate, index) => {
      const original = currencyMarkupRates[index]
      return rate.newDepositRate !== original.rate || rate.newWithdrawalRate !== original.rate
    })

    const requestPayload = {
      newCurrencyRatesList: changedRates.map((rate) => ({
        baseCurrency: rate.baseCurrency,
        quoteCurrency: rate.quoteCurrency,
        newDepositRate: rate.newDepositRate,
        newWithdrawalRate: rate.newWithdrawalRate,
      })),
    }

    if (requestPayload.newCurrencyRatesList.length === 0) {
      return
    }

    try {
      await exchangeRatesApiClient
        .updateCurrencyRates(quoteCurrency, requestPayload)
        .then(() => setToast(successToast('Currency Rates updated successfully.')))
    } catch (e) {
      setToast(errorToast('Failed to update rates.' + e))
    }
    await onClickFetch(quoteCurrency)
  }

  const hasChanges = () => {
    return editableRates.some((rate, index) => {
      const original = currencyMarkupRates[index]
      if (!original) return false
      return rate.newDepositRate !== original.rate || rate.newWithdrawalRate !== original.rate
    })
  }

  const handleItemChange = (e: GridItemChangeEvent) => {
    const updated = editableRates.map((item) =>
      item === e.dataItem && e.field
        ? { ...item, [e.field]: e.value.replace(NumbersAndDotOnlyPattern, '') }
        : item
    )
    setEditableRates(updated)
    validateRates(updated)
  }

  const handleCellClick = (dataItem: RateFormData, field: string, finalValue?: string) => {
    const updated = editableRates.map((item) => {
      if (item === dataItem) {
        const updates: Partial<RateFormData> = { inEdit: field || undefined }

        if (finalValue !== undefined && field === '') {
          const editedField = item.inEdit
          if (editedField === 'newDepositRate' || editedField === 'newWithdrawalRate') {
            updates[editedField] = Number(finalValue)
          }
        }

        if (field === '' && item.inEdit === 'newDepositRate') {
          const depositRate = finalValue ? Number(finalValue) : item.newDepositRate
          updates.newWithdrawalRate = depositRate * (1 - (withdrawalPercentage ?? 0) / 100)
        }

        return { ...item, ...updates }
      }
      return { ...item, inEdit: undefined }
    })

    setEditableRates(updated)

    if (field === '') {
      validateRates(updated)
    }
  }

  return {
    hasChanges,
    handleItemChange,
    handleCellClick,
    handleSubmit,
    onClickFetch,
    editableRates,
    invalidRateMessage,
  }
}
